import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Circle, Play, Pause, Heart, Code, Clock, GitCommit } from 'lucide-react';
import { Button } from '../ui/button';

const TUTORIAL_SLIDES = [
  {
    id: 1,
    image: 'https://customer-assets.emergentagent.com/job_e14b33b3-a034-4c07-a767-197bd3c4c575/artifacts/mt1hhw9h_IMG-20260302-WA0010.jpg',
    title: 'The Solution',
    subtitle: 'WriteRight PCM+ People First',
    description: 'Documentation made simple. Your administrative assistant awaits.',
  },
  {
    id: 2,
    image: 'https://customer-assets.emergentagent.com/job_e14b33b3-a034-4c07-a767-197bd3c4c575/artifacts/mgqewt64_1773721426776.png',
    title: '"Doc, Can You Help?"',
    subtitle: 'A Real Story',
    description: 'Soldier had knee surgery. Wife needs FMLA to care for the kids. She doesn\'t know how to ask.',
  },
  {
    id: 3,
    image: 'https://customer-assets.emergentagent.com/job_e14b33b3-a034-4c07-a767-197bd3c4c575/artifacts/o7lgpkgc_1773716567952.png',
    title: 'Both Sides Working Together',
    subtitle: 'The Bridge',
    description: 'We need to make sure the caregiver has the documentation they need. It\'s a tough situation for the family.',
  },
  {
    id: 4,
    image: 'https://customer-assets.emergentagent.com/job_e14b33b3-a034-4c07-a767-197bd3c4c575/artifacts/4izlz28q_1773721512080.png',
    title: 'That\'s Why I Created a Link',
    subtitle: 'The Solution',
    description: 'Tell her to call me. I\'ll get her the forms and a personal note for her HR. We\'ll handle it.',
  },
  {
    id: 5,
    image: 'https://customer-assets.emergentagent.com/job_e14b33b3-a034-4c07-a767-197bd3c4c575/artifacts/gf38a4xf_1773689653319.png',
    title: 'The Link',
    subtitle: 'Care Without the Countdown',
    description: 'The care they deserve. The time you don\'t have. WriteRight PCM+ bridges the gap.',
  },
  {
    id: 6,
    image: 'https://customer-assets.emergentagent.com/job_e14b33b3-a034-4c07-a767-197bd3c4c575/artifacts/af4x9h2c_1773719582034.png',
    title: 'WriteRight PCM+',
    subtitle: 'The Connection',
    description: 'Doctor and NCO working together through WriteRight PCM+. People First.',
  },
  {
    id: 7,
    image: null,
    title: 'Built for Both Sides',
    subtitle: 'Administrative Help on Both Sides of the Stethoscope',
    isBenefitsSlide: true,
    patientBenefits: [
      'Draft portal messages for MHS Genesis or MyChart',
      'VA Claims wizard walks you through disability filings',
      'Type your story at home, in your own words',
      'Pre-built templates for 16+ common scenarios',
      'High Contrast mode for accessibility'
    ],
    providerBenefits: [
      'Multiple document formats in seconds',
      'ICD-10 code detection & validation',
      'AR 25-50 compliant memo formats',
      'Copy All from the toolbar in one click',
      'Time saved = patients seen'
    ]
  },
  {
    id: 8,
    image: 'https://customer-assets.emergentagent.com/job_e14b33b3-a034-4c07-a767-197bd3c4c575/artifacts/loodyjju_1773697527656.png',
    title: 'The Care You Deserve',
    subtitle: 'The Better Encounter',
    description: 'Your voice heard. Your care needs met. Your time valued.',
  },
  {
    id: 9,
    image: 'https://customer-assets.emergentagent.com/job_e14b33b3-a034-4c07-a767-197bd3c4c575/artifacts/mt1hhw9h_IMG-20260302-WA0010.jpg',
    title: 'Ready to Start?',
    subtitle: 'WriteRight PCM+ People First',
    isClosingSlide: true,
    closingLines: [
      'Your administrative assistant is ready.',
      'Quality care AND timely charting.',
      'Finally.'
    ]
  },
];

export const TutorialSlides = ({ onClose, onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(null);

  // Auto-advance slides
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => {
        if (prev >= TUTORIAL_SLIDES.length - 1) {
          setIsAutoPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 5000);

    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  const nextSlide = () => {
    // Loop back to beginning if at last slide
    if (currentSlide < TUTORIAL_SLIDES.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      setCurrentSlide(0); // Loop back to start
    }
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    } else {
      setCurrentSlide(TUTORIAL_SLIDES.length - 1); // Loop back to last slide
    }
    setIsAutoPlaying(false);
  };

  // Touch/swipe handlers
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
    setTouchStart(null);
  };

  const slide = TUTORIAL_SLIDES[currentSlide];
  const isLastSlide = currentSlide === TUTORIAL_SLIDES.length - 1;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/95 flex flex-col">
      {/* Header with Skip button - Fixed at top with solid background */}
      <div className="flex items-center justify-between p-3 sm:p-4 bg-black border-b border-white/10 sticky top-0 z-20">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            {isAutoPlaying ? (
              <Pause className="w-4 h-4 text-white" />
            ) : (
              <Play className="w-4 h-4 text-white" />
            )}
          </button>
          <span className="text-white/60 text-sm">
            {currentSlide + 1} / {TUTORIAL_SLIDES.length}
          </span>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Skip button */}
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white text-sm px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors font-medium"
          >
            Skip
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Slide Content - Scrollable with proper spacing - TAP TO ADVANCE */}
      <div 
        className="flex-1 flex flex-col items-center justify-start p-4 pt-2 pb-44 overflow-y-auto cursor-pointer"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={nextSlide}
        title="Tap to continue"
      >
        <p className="text-white/50 text-xs mb-2 text-center">Tap anywhere to continue →</p>
        <div className="max-w-5xl w-full animate-in fade-in duration-500">
          {/* Benefits Slide - Two Columns */}
          {slide.isBenefitsSlide ? (
            <div className="text-center space-y-6 px-4 py-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-white">
                {slide.title}
              </h2>
              <p className="text-amber-400 font-medium">
                {slide.subtitle}
              </p>
              
              {/* Two Column Benefits */}
              <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mt-6">
                {/* Patient Benefits */}
                <div 
                  className="p-5 rounded-xl text-left"
                  style={{
                    background: 'linear-gradient(135deg, #1e3a5f 0%, #0f1f33 100%)',
                    border: '2px solid #3b82f6',
                    boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)'
                  }}
                >
                  <h3 className="text-lg font-bold text-blue-400 mb-4 flex items-center gap-2">
                    <span className="text-2xl">🩺</span> For Patients
                  </h3>
                  <ul className="space-y-3">
                    {slide.patientBenefits?.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2 text-white/90">
                        <span className="text-blue-400 mt-0.5">✓</span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Provider Benefits */}
                <div 
                  className="p-5 rounded-xl text-left"
                  style={{
                    background: 'linear-gradient(135deg, #3d1f0d 0%, #1f0f05 100%)',
                    border: '2px solid #d97706',
                    boxShadow: '0 4px 20px rgba(217, 119, 6, 0.3)'
                  }}
                >
                  <h3 className="text-lg font-bold text-amber-400 mb-4 flex items-center gap-2">
                    <span className="text-2xl">⚕️</span> For Providers
                  </h3>
                  <ul className="space-y-3">
                    {slide.providerBenefits?.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2 text-white/90">
                        <span className="text-amber-400 mt-0.5">✓</span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : slide.isClosingSlide ? (
            /* Closing Slide */
            <div className="text-center space-y-6 px-4 py-6">
              {slide.image && (
                <div className="relative w-full max-w-2xl mx-auto rounded-xl overflow-hidden shadow-2xl">
                  <img 
                    src={slide.image} 
                    alt={slide.title}
                    className="w-full h-auto object-contain"
                    style={{ maxHeight: '35vh' }}
                  />
                </div>
              )}
              <div className="space-y-4">
                <h2 className="text-3xl sm:text-4xl font-bold text-white">
                  {slide.title}
                </h2>
                <p className="text-amber-400 font-medium text-xl">
                  {slide.subtitle}
                </p>
                {slide.closingLines ? (
                  <div className="space-y-2 max-w-xl mx-auto">
                    {slide.closingLines.map((line, li) => (
                      <p key={li} className={`text-lg ${li === slide.closingLines.length - 1 ? 'text-amber-400 font-bold text-2xl mt-3' : 'text-white/90'}`}>
                        {line}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/90 text-lg max-w-xl mx-auto">
                    {slide.description}
                  </p>
                )}
                <Button
                  onClick={onClose}
                  className="mt-6 px-8 py-3 text-lg font-semibold bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black rounded-xl shadow-lg"
                >
                  Get Started Now
                </Button>
              </div>
            </div>
          ) : slide.isOriginStory ? (
            /* Origin Story Special Slide */
            <div className="text-center space-y-6 px-4 py-8">
              {/* Built in 30 Days Badge */}
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-amber-500/20 via-red-500/20 to-blue-500/20 border border-amber-500/30">
                <Heart className="w-5 h-5 text-red-400" />
                <span className="text-xl font-bold text-white">Built in 30 Days</span>
                <Code className="w-5 h-5 text-blue-400" />
              </div>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <Clock className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                  <p className="text-2xl font-bold text-white">{slide.stats.days}</p>
                  <p className="text-xs text-white/60">Days</p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <Code className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                  <p className="text-2xl font-bold text-white">{slide.stats.lines}</p>
                  <p className="text-xs text-white/60">Lines of Code</p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <GitCommit className="w-8 h-8 mx-auto mb-2 text-green-400" />
                  <p className="text-2xl font-bold text-white">{slide.stats.commits}</p>
                  <p className="text-xs text-white/60">Commits</p>
                </div>
              </div>
              
              {/* Title & Story */}
              <div className="space-y-4 max-w-xl mx-auto">
                <h2 className="text-2xl sm:text-3xl font-bold text-white">
                  {slide.title}
                </h2>
                <p className="text-amber-400 font-medium text-lg">
                  {slide.subtitle}
                </p>
                <p className="text-white/80 text-base leading-relaxed">
                  {slide.description}
                </p>
                
                {/* Hourglass Symbolism */}
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-xs text-amber-400 font-medium mb-1">⏳ The Hourglass</p>
                  <p className="text-xs text-white/70">{slide.extendedStory?.hourglass}</p>
                </div>
                
                {/* Extended Story */}
                <div className="text-left space-y-2 p-4 bg-white/5 rounded-xl border border-white/10 text-sm text-white/70">
                  <p>{slide.extendedStory?.mission}</p>
                  <p>{slide.extendedStory?.reality}</p>
                  <p className="text-amber-400 font-medium">{slide.extendedStory?.resolve}</p>
                </div>
                
                {/* Quote */}
                <div className="p-4 bg-amber-500/10 border-l-4 border-amber-400 rounded-r-lg italic text-white/90">
                  "Because the system won't change — but your workflow can. Serve the patient. Survive the system. Do both without compromise."
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Image - Larger and scrollable */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-4 bg-gray-900">
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="w-full h-auto object-contain"
                  style={{ maxHeight: '65vh' }}
                />
              </div>

              {/* Text Content - Below image */}
              <div className="text-center space-y-2 px-4 py-3 bg-black/50 rounded-xl">
                <h2 className="text-xl sm:text-2xl font-bold text-white">
                  {slide.title}
                </h2>
                <p className="text-amber-400 font-medium text-base">
                  {slide.subtitle}
                </p>
                <p className="text-white/80 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
                  {slide.description}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Navigation - Fixed at bottom with padding for Made with Emergent badge */}
      <div className="absolute bottom-0 left-0 right-0 p-4 pb-16 bg-gradient-to-t from-black via-black/90 to-transparent">
        {/* Progress Dots */}
        <div className="flex items-center justify-center gap-2 mb-3">
          {TUTORIAL_SLIDES.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 ${
                index === currentSlide
                  ? 'w-8 h-2 bg-amber-400 rounded-full'
                  : 'w-2 h-2 bg-white/40 rounded-full hover:bg-white/60'
              }`}
            />
          ))}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between max-w-md mx-auto">
          <Button
            variant="ghost"
            onClick={prevSlide}
            disabled={false}
            className="text-white hover:bg-white/10 disabled:opacity-30"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back
          </Button>

          {isLastSlide ? (
            <div className="flex items-center gap-2">
              <Button
                onClick={onComplete || onClose}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-6"
              >
                Get Started
              </Button>
              <Button
                onClick={nextSlide}
                variant="ghost"
                className="text-white hover:bg-white/10"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          ) : (
            <Button
              onClick={nextSlide}
              className="bg-white/20 hover:bg-white/30 text-white"
            >
              Next
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          )}
        </div>
      </div>

      {/* Side Navigation Arrows (desktop) */}
      <button
        onClick={prevSlide}
        disabled={false}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 transition-all"
      >
        <ChevronLeft className="w-6 h-6 text-white" />
      </button>
      
      <button
        onClick={nextSlide}
        disabled={false}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 transition-all"
      >
        <ChevronRight className="w-6 h-6 text-white" />
      </button>
    </div>
  );
};

export default TutorialSlides;
